import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { AuthService } from '../services/auth.service';
import { SessionService } from '../services/session.service';

// PrimeNG Imports
import { CardModule } from 'primeng/card';
import { TagModule } from 'primeng/tag';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { ButtonModule } from 'primeng/button';
import { PanelModule } from 'primeng/panel';
import { DividerModule } from 'primeng/divider';
import { ChipModule } from 'primeng/chip';
import { BadgeModule } from 'primeng/badge';
import { AvatarModule } from 'primeng/avatar';
import { MessageModule } from 'primeng/message';

interface SessionSummary {
  sessionId: string;
  mediaCount: number;
  zoomSessionId: string;
  hasRecording: boolean;
  duration: number;
  formattedDuration?: string;
}

@Component({
    selector: 'app-session-end',
    imports: [
        CommonModule,
        CardModule,
        TagModule,
        ProgressSpinnerModule,
        ButtonModule,
        PanelModule,
        DividerModule,
        ChipModule,
        BadgeModule,
        AvatarModule,
        MessageModule
    ],
    templateUrl: './session-end.component.html',
    styleUrl: './session-end.component.scss'
})
export class SessionEndComponent implements OnInit {
  sessionSummary: SessionSummary | null = null;
  userRole: string = '';
  loading: boolean = true;
  error: string = '';
  currentDateTime: string = '';

  constructor(private authService: AuthService, private sessionService: SessionService) {}

  async ngOnInit() {
    try {
      this.currentDateTime = new Date().toLocaleString();
      
      // Determine user role from token before logout
      const token = localStorage.getItem('token');
      if (token) {
        try {
          const payload = JSON.parse(atob(token.split('.')[1]));
          this.userRole = payload.role || 'inspector';
        } catch (e) {
          this.userRole = 'inspector'; // Default to inspector
        }
      }

      // Get session details before logout
      try {
        this.sessionSummary = await this.sessionService.getSummary();
        
        if (this.sessionSummary) {
          this.sessionSummary.formattedDuration = this.formatDuration(this.sessionSummary.duration);
        }
      } catch (summaryError) {
        console.warn('Could not get session summary, using fallback data:', summaryError);
        // Fallback: create a basic summary from available data
        this.sessionSummary = {
          sessionId: this.sessionService.sessionId || 'N/A',
          mediaCount: this.sessionService.imageCount || 0,
          zoomSessionId: this.sessionService.zoomSessionId || '',
          hasRecording: false,
          duration: 0,
          formattedDuration: 'Session completed'
        };
      }
      
      console.log('Session Summary:', this.sessionSummary);
      console.log('User Role:', this.userRole);
      
      // Logout after getting session data
      this.authService.logout();
      
    } catch (error) {
      console.error('Error during session end initialization:', error);
      this.error = 'Failed to load session summary. Please contact support if this issue persists.';
      
      // Create minimal fallback summary even when there's an error
      this.sessionSummary = {
        sessionId: 'N/A',
        mediaCount: 0,
        zoomSessionId: '',
        hasRecording: false,
        duration: 0,
        formattedDuration: 'Session completed'
      };
      
      this.authService.logout(); // Still logout even if there's an error
    } finally {
      this.loading = false;
    }
  }

  private formatDuration(durationInMinutes: number): string {
    if (!durationInMinutes || durationInMinutes < 1) {
      return 'Less than 1 minute';
    }
    
    const hours = Math.floor(durationInMinutes / 60);
    const minutes = Math.round(durationInMinutes % 60);
    
    if (hours > 0) {
      return `${hours} hour${hours > 1 ? 's' : ''} and ${minutes} minute${minutes !== 1 ? 's' : ''}`;
    } else {
      return `${minutes} minute${minutes !== 1 ? 's' : ''}`;
    }
  }

  get isCoordinator(): boolean {
    return this.userRole === 'coordinator';
  }

  get isInspector(): boolean {
    return this.userRole === 'inspector';
  }
}
